import React from 'react';
import { ArrowRight, User, Building2, DollarSign, Package, Shield, Clock, HelpCircle } from 'lucide-react';

const ObligationVisualizer = ({ data }) => {
  if (!data || !data.parties || !data.obligations) {
    return (
      <div className="text-center py-8 text-slate-400">
        <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No hay datos de visualización disponibles</p>
      </div>
    );
  }

  const { parties, obligations, timeline } = data;

  const getObligationIcon = (type) => {
    switch (type) {
      case 'pago':
        return <DollarSign className="w-4 h-4" />;
      case 'entrega':
        return <Package className="w-4 h-4" />;
      case 'servicio':
        return <Building2 className="w-4 h-4" />;
      case 'garantía':
        return <Shield className="w-4 h-4" />;
      default:
        return <ArrowRight className="w-4 h-4" />;
    }
  };

  const getObligationColor = (type) => {
    switch (type) {
      case 'pago':
        return 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400';
      case 'entrega':
        return 'bg-blue-500/20 border-blue-500/40 text-blue-400';
      case 'servicio':
        return 'bg-purple-500/20 border-purple-500/40 text-purple-400';
      case 'garantía':
        return 'bg-amber-500/20 border-amber-500/40 text-amber-400';
      default:
        return 'bg-slate-500/20 border-slate-500/40 text-slate-400';
    }
  };

  const partyColors = ['bg-blue-500', 'bg-cyan-500', 'bg-purple-500', 'bg-rose-500'];

  return (
    <div className="space-y-6" data-testid="obligation-visualizer">
      {/* Parties Section */}
      <div>
        <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center space-x-2">
          <User className="w-4 h-4" />
          <span>Partes del Contrato</span>
        </h4>
        <div className="grid grid-cols-2 gap-4">
          {parties.map((party, index) => (
            <div
              key={party.id}
              className="bg-slate-900/50 border border-slate-700 rounded-lg p-4"
              data-testid={`party-${party.id}`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${partyColors[index % partyColors.length]}`} />
                <div>
                  <p className="font-medium text-white">{party.name}</p>
                  <p className="text-xs text-slate-400">{party.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Obligations Flow */}
      <div>
        <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center space-x-2">
          <ArrowRight className="w-4 h-4" />
          <span>Flujo de Obligaciones</span>
        </h4>
        <div className="space-y-3">
          {obligations.map((obligation, index) => {
            const fromParty = parties.find(p => p.id === obligation.from);
            const toParty = parties.find(p => p.id === obligation.to);
            const fromIndex = parties.findIndex(p => p.id === obligation.from);
            const toIndex = parties.findIndex(p => p.id === obligation.to);

            return (
              <div
                key={index}
                className={`p-4 rounded-lg border ${getObligationColor(obligation.type)}`}
                data-testid={`obligation-${index}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getObligationIcon(obligation.type)}
                    <span className="text-xs uppercase tracking-wide">{obligation.type}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${partyColors[fromIndex % partyColors.length]}`} />
                    <span className="text-white">{fromParty?.name || obligation.from}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-500" />
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${partyColors[toIndex % partyColors.length]}`} />
                    <span className="text-white">{toParty?.name || obligation.to}</span>
                  </div>
                </div>
                <p className="mt-2 text-sm text-slate-300">{obligation.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeline Section */}
      {timeline && timeline.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>Línea de Tiempo</span>
          </h4>
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-700" />
            <div className="space-y-4">
              {timeline.map((event, index) => (
                <div key={index} className="flex items-start space-x-4 relative" data-testid={`timeline-${index}`}>
                  <div className="w-8 h-8 rounded-full bg-slate-800 border-2 border-blue-500 flex items-center justify-center z-10">
                    <span className="text-xs text-blue-400">{index + 1}</span>
                  </div>
                  <div className="flex-1 bg-slate-900/50 border border-slate-700 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-white text-sm">{event.event}</span>
                      <span className="text-xs text-slate-400">{event.date}</span>
                    </div>
                    <p className="text-xs text-slate-400">Responsable: {event.party}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Visual Legend */}
      <div className="bg-slate-900/30 rounded-lg p-4">
        <h4 className="text-xs font-medium text-slate-500 mb-2">Leyenda de Tipos de Obligación</h4>
        <div className="flex flex-wrap gap-2">
          {['pago', 'entrega', 'servicio', 'garantía', 'otro'].map((type) => (
            <div key={type} className={`flex items-center space-x-1 px-2 py-1 rounded border text-xs ${getObligationColor(type)}`}>
              {getObligationIcon(type)}
              <span className="capitalize">{type}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ObligationVisualizer;
